import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  useGetTagsQuery,
  useGetContactsByTagQuery,
  useCreateCampaignMutation,
  useGetActiveCampaignQuery,
  CampaignCreate,
} from '../../store/api/outboundCallingApi';
import { useGetCurrentUserQuery } from '../../store/api/userApi';
import { TimezoneSelect } from '../TimezoneSelect';

const schema = yup.object().shape({
  name: yup.string().required('Campaign name is required').max(500),
  tag_name: yup.string().required('Tag is required'),
  agent_id: yup.string().required('Agent is required'),
  umbrella_account_id: yup.string().optional(), // Made optional since we get it from agent
  interval_minutes: yup.number().min(1).max(60).required(),
  timezone: yup.string().required('Timezone is required'),
  start_hour: yup.number().min(0).max(23).required(),
  end_hour: yup.number().min(0).max(23).required(),
  max_call_duration_minutes: yup.number().min(1).max(60).required(),
  max_retries: yup.number().min(0).max(10).required(),
  days_of_week: yup.array().of(yup.string()).min(1, 'Select at least one day').required(),
});

const daysOfWeek = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

interface NewCampaignFormProps {
  onSuccess: () => void;
  onCancel?: () => void;
}

const NewCampaignForm: React.FC<NewCampaignFormProps> = ({ onSuccess, onCancel }) => {
    // Get current user info (for umbrella_user_id)
    const { data: currentUser, isLoading: userLoading } = useGetCurrentUserQuery();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [selectedDays, setSelectedDays] = useState<string[]>(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);
  
  // Get agent_id and account_id from URL parameters
  const agentIdFromUrl = searchParams.get('agent_id');
  const accountIdFromUrl = searchParams.get('account_id');
  const selectedAgentId = agentIdFromUrl ? parseInt(agentIdFromUrl) : null;
  const selectedAccountId = accountIdFromUrl ? parseInt(accountIdFromUrl) : null;
  
  // Check for existing active campaign for this agent
  const { data: existingCampaign, isLoading: checkingExisting } = useGetActiveCampaignQuery(
    selectedAgentId || 0,
    { skip: !selectedAgentId }
  );
  
  // Debug logging
  console.log('Agent & Account Debug:', {
    agentIdFromUrl,
    accountIdFromUrl,
    selectedAgentId,
    selectedAccountId,
    searchParams: Object.fromEntries(searchParams.entries())
  });

  // Generate auto-populated campaign name
  const generateCampaignName = () => {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', { 
      month: 'short', 
      day: '2-digit', 
      year: 'numeric' 
    });
    const timeStr = now.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    return `Campaign - ${dateStr} - ${timeStr}`;
  };

  const { control, handleSubmit, formState: { errors, isValid, isDirty }, setValue, watch } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange', // Enable real-time validation
    defaultValues: {
      name: generateCampaignName(),
      tag_name: '',
      agent_id: selectedAgentId ? selectedAgentId.toString() : '',
      umbrella_account_id: selectedAccountId ? selectedAccountId.toString() : '',
      interval_minutes: 10,
      timezone: 'America/New_York',
      start_hour: 9,
      end_hour: 17,
      max_call_duration_minutes: 5,
      max_retries: 3,
      days_of_week: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    },
  });

  // API queries
  // API queries
  const umbrellaUserId = currentUser?.umbrella_user_id;
  const shouldFetchTags = !!umbrellaUserId;
  const { data: tags, isLoading: tagsLoading, refetch: refetchTags, isFetching: tagsFetching, error: tagsError } = useGetTagsQuery(umbrellaUserId, { skip: !shouldFetchTags });
  
  // Debug logging
  console.log('Tags Debug:', { tags, tagsLoading, tagsFetching });
  const { data: contacts, isLoading: contactsLoading } = useGetContactsByTagQuery(selectedTag, {
    skip: !selectedTag,
  });

  // Mutations
  const [createCampaign, { isLoading: creating }] = useCreateCampaignMutation();

  const watchedTag = watch('tag_name');
  const watchedAgentId = watch('agent_id');
  const watchedDays = watch('days_of_week');

  useEffect(() => {
    if (watchedTag) {
      setSelectedTag(watchedTag);
    }
  }, [watchedTag]);

  // Set agent_id and account_id from URL parameters when component mounts
  useEffect(() => {
    console.log('=== AGENT & ACCOUNT EFFECT ===');
    console.log('selectedAgentId:', selectedAgentId);
    console.log('selectedAccountId:', selectedAccountId);
    console.log('agentIdFromUrl:', agentIdFromUrl);
    console.log('accountIdFromUrl:', accountIdFromUrl);
    
    if (selectedAgentId) {
      setValue('agent_id', selectedAgentId.toString());
      console.log('Setting agent_id to:', selectedAgentId.toString());
    } else {
      console.log('No selectedAgentId, agent_id will be empty');
    }
    
    if (selectedAccountId) {
      setValue('umbrella_account_id', selectedAccountId.toString());
      console.log('Setting umbrella_account_id to:', selectedAccountId.toString());
    } else {
      console.log('No selectedAccountId, umbrella_account_id will be empty');
    }
    console.log('==============================');
  }, [selectedAgentId, selectedAccountId, setValue]);

  // Force refetch tags on component mount to ensure fresh data
  useEffect(() => {
    // Force a complete refetch with cache invalidation
    refetchTags();
  }, [refetchTags]);

  // Also refetch when component becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refetchTags();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refetchTags]);

  // Sync selectedDays with form field
  useEffect(() => {
    setValue('days_of_week', selectedDays);
  }, [selectedDays, setValue]);

  // Initialize form field on mount
  useEffect(() => {
    console.log('=== DAYS INITIALIZATION ===');
    console.log('selectedDays:', selectedDays);
    setValue('days_of_week', selectedDays);
    console.log('Set days_of_week to:', selectedDays);
    console.log('==========================');
  }, [setValue]); // Only run once on mount

  const handleDayToggle = (day: string) => {
    console.log('=== DAY TOGGLE ===');
    console.log('Toggling day:', day);
    console.log('Current selectedDays:', selectedDays);
    
    const newDays = selectedDays.includes(day) 
      ? selectedDays.filter(d => d !== day)
      : [...selectedDays, day];
    
    console.log('New days:', newDays);
    setSelectedDays(newDays);
    setValue('days_of_week', newDays); // Update the form field
    console.log('Updated form field days_of_week to:', newDays);
    console.log('==================');
  };

  const handleRefreshCampaignName = () => {
    const newName = generateCampaignName();
    setValue('name', newName);
  };


  // Check if form is ready for submission
  const isFormReady = () => {
    // Get all form values
    const formValues = watch();
    
    // Check all required fields from schema with comprehensive validation
    const hasName = formValues.name && formValues.name.trim() !== '';
    const hasTag = formValues.tag_name && formValues.tag_name.trim() !== '';
    const hasAgent = formValues.agent_id && formValues.agent_id.trim() !== '';
    const hasInterval = formValues.interval_minutes && formValues.interval_minutes > 0 && formValues.interval_minutes <= 60;
    const hasTimezone = formValues.timezone && formValues.timezone.trim() !== '';
    const hasStartHour = formValues.start_hour !== undefined && formValues.start_hour >= 0 && formValues.start_hour <= 23;
    const hasEndHour = formValues.end_hour !== undefined && formValues.end_hour >= 0 && formValues.end_hour <= 23;
    const hasMaxDuration = formValues.max_call_duration_minutes && formValues.max_call_duration_minutes > 0 && formValues.max_call_duration_minutes <= 60;
    const hasMaxRetries = formValues.max_retries !== undefined && formValues.max_retries >= 0 && formValues.max_retries <= 10;
    const hasDays = formValues.days_of_week && formValues.days_of_week.length > 0;
    
    // Additional validation for hour range
    const validHourRange = hasStartHour && hasEndHour && formValues.start_hour < formValues.end_hour;
    
    const ready = hasName && hasTag && hasAgent && hasInterval && hasTimezone && 
                  hasStartHour && hasEndHour && hasMaxDuration && hasMaxRetries && 
                  hasDays && validHourRange && !creating && !tagsLoading;
    
    // Debug logging
    console.log('=== FORM VALIDATION DEBUG ===');
    console.log('Form Values:', formValues);
    console.log('hasName:', hasName);
    console.log('hasTag:', hasTag);
    console.log('hasAgent:', hasAgent);
    console.log('hasInterval:', hasInterval);
    console.log('hasTimezone:', hasTimezone);
    console.log('hasStartHour:', hasStartHour);
    console.log('hasEndHour:', hasEndHour);
    console.log('hasMaxDuration:', hasMaxDuration);
    console.log('hasMaxRetries:', hasMaxRetries);
    console.log('hasDays:', hasDays);
    console.log('validHourRange:', validHourRange);
    console.log('creating:', creating);
    console.log('tagsLoading:', tagsLoading);
    console.log('ready:', ready);
    console.log('=============================');
    
    return ready;
  };

  const onSubmit = async (data: any) => {
    try {
      // Navigate to preview page with form data
      const formData = data as CampaignCreate;
      const searchParams = new URLSearchParams({
        campaign_name: formData.name,
        tag_name: formData.tag_name,
        agent_id: formData.agent_id.toString(),
        umbrella_account_id: formData.umbrella_account_id.toString(),
        interval_minutes: formData.interval_minutes.toString(),
        timezone: formData.timezone,
        start_hour: formData.start_hour.toString(),
        end_hour: formData.end_hour.toString(),
        max_call_duration_minutes: formData.max_call_duration_minutes.toString(),
        max_retries: formData.max_retries.toString(),
        days_of_week: formData.days_of_week.join(',')
      });
      
      navigate(`/outbound-calling/preview?${searchParams.toString()}`);
    } catch (error: any) {
      console.error('Error preparing preview:', error);
    }
  };

  if (userLoading || tagsLoading || checkingExisting) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading user and tags...
        </Typography>
      </Box>
    );
  }

  // Show warning if agent already has an active campaign
  if (existingCampaign) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Agent Already Has Active Campaign
          </Typography>
          <Typography variant="body2" paragraph>
            Agent {selectedAgentId} already has an active or paused campaign: <strong>{existingCampaign.name}</strong>
          </Typography>
          <Typography variant="body2" paragraph>
            Status: <Chip label={existingCampaign.status.toUpperCase()} color={existingCampaign.status === 'active' ? 'success' : 'warning'} size="small" />
          </Typography>
          <Typography variant="body2" paragraph>
            You cannot create a new campaign until the existing one is stopped or completed.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/outbound-calling')}
              sx={{ mr: 2 }}
            >
              View Campaigns
            </Button>
            <Button 
              variant="contained" 
              onClick={() => navigate(`/outbound-calling?agent_id=${selectedAgentId}`)}
            >
              Manage This Agent's Campaigns
            </Button>
          </Box>
        </Alert>
      </Box>
    );
  }

  // Show error if no data is available or API errored
  if (tagsError) {
    let errorMsg = 'Unknown error';
    if (tagsError) {
      if ('message' in tagsError && typeof tagsError.message === 'string') {
        errorMsg = tagsError.message;
      } else if ('status' in tagsError && 'data' in tagsError) {
        errorMsg = `Status: ${tagsError.status}, Data: ${JSON.stringify(tagsError.data)}`;
      } else if (typeof tagsError === 'string') {
        errorMsg = tagsError;
      }
    }
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading tags: {errorMsg}
      </Alert>
    );
  }
  if (!tags || tags.length === 0) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        No tags found. Please ensure you have tags configured in your Umbrella account.<br />
        <span style={{ fontSize: '0.9em', color: '#888' }}>
          (Debug: umbrella_user_id = {umbrellaUserId ? umbrellaUserId : 'undefined'})
        </span>
      </Alert>
    );
  }


  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ py: 1 }}>
      {/* Form Validation Summary */}
      {Object.keys(errors).length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Please fill in all required fields to create the campaign.
        </Alert>
      )}
      
      {/* Validation Summary */}
      {!isFormReady() && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Complete the following fields to preview contacts:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {(!watch('name') || !watch('name')?.trim()) && <li>Campaign Name</li>}
            {(!watch('tag_name') || !watch('tag_name')?.trim()) && <li>Tag</li>}
            {(!watch('agent_id') || !watch('agent_id')?.trim()) && <li>Agent</li>}
            {(!watch('interval_minutes') || watch('interval_minutes') <= 0 || watch('interval_minutes') > 60) && <li>Interval (minutes) - must be between 1-60</li>}
            {(!watch('timezone') || !watch('timezone')?.trim()) && <li>Timezone</li>}
            {(watch('start_hour') === undefined || watch('start_hour') < 0 || watch('start_hour') > 23) && <li>Start Hour (0-23)</li>}
            {(watch('end_hour') === undefined || watch('end_hour') < 0 || watch('end_hour') > 23) && <li>End Hour (0-23)</li>}
            {(watch('start_hour') !== undefined && watch('end_hour') !== undefined && watch('start_hour') >= watch('end_hour')) && <li>Start Hour must be before End Hour</li>}
            {(!watch('max_call_duration_minutes') || watch('max_call_duration_minutes') <= 0 || watch('max_call_duration_minutes') > 60) && <li>Max Call Duration (minutes) - must be between 1-60</li>}
            {(watch('max_retries') === undefined || watch('max_retries') < 0 || watch('max_retries') > 10) && <li>Max Retries (0-10)</li>}
            {(!watch('days_of_week') || !watch('days_of_week')?.length || watch('days_of_week').length === 0) && <li>Days of Week (select at least one day)</li>}
          </Box>
        </Alert>
      )}
      
      <Grid container spacing={1}>
        {/* Campaign Details */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Campaign Details
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label="Campaign Name"
                error={!!errors.name}
                helperText={errors.name?.message || 'Enter a descriptive name for your campaign'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleRefreshCampaignName}
                        edge="end"
                        title="Refresh timestamp"
                        size="small"
                      >
                        <Refresh />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="tag_name"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!errors.tag_name}>
                <InputLabel>Tag</InputLabel>
                <Select 
                  {...field} 
                  label="Tag"
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => refetchTags()}
                        edge="end"
                        title="Refresh tags"
                        size="small"
                        disabled={tagsFetching}
                      >
                        {tagsFetching ? <CircularProgress size={16} /> : <Refresh />}
                      </IconButton>
                    </InputAdornment>
                  }
                >
                  {tagsLoading ? (
                    <MenuItem disabled>Loading tags...</MenuItem>
                  ) : tags && tags.length > 0 ? (
                    tags.slice().sort((a, b) => a.tag_name.localeCompare(b.tag_name)).map((tag) => (
                      <MenuItem key={tag.tag_name} value={tag.tag_name}>
                        {tag.tag_name} ({tag.contact_count} contacts)
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No tags found</MenuItem>
                  )}
                </Select>
                {errors.tag_name && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.tag_name.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        </Grid>


        {/* Agent Info Display */}
        {(selectedAgentId || selectedAccountId) && (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Creating campaign for Agent ID: {selectedAgentId} 
                {selectedAccountId && ` (Account ID: ${selectedAccountId})`}
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* Calling Configuration */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Calling Configuration
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="interval_minutes"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                type="number"
                label="Interval (minutes)"
                inputProps={{ min: 1, max: 60 }}
                error={!!errors.interval_minutes}
                helperText={errors.interval_minutes?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="max_call_duration_minutes"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                type="number"
                label="Max Call Duration (minutes)"
                inputProps={{ min: 1, max: 60 }}
                error={!!errors.max_call_duration_minutes}
                helperText={errors.max_call_duration_minutes?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="max_retries"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                type="number"
                label="Max Retries"
                inputProps={{ min: 0, max: 10 }}
                error={!!errors.max_retries}
                helperText={errors.max_retries?.message}
              />
            )}
          />
        </Grid>

        {/* Timezone and Hours */}
        <Grid item xs={12}>
          <Controller
            name="timezone"
            control={control}
            render={({ field }) => (
              <TimezoneSelect
                value={field.value}
                onChange={field.onChange}
                error={!!errors.timezone}
                helperText={errors.timezone?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="start_hour"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                type="number"
                label="Start Hour"
                inputProps={{ min: 0, max: 23 }}
                error={!!errors.start_hour}
                helperText={errors.start_hour?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="end_hour"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                type="number"
                label="End Hour"
                inputProps={{ min: 0, max: 23 }}
                error={!!errors.end_hour}
                helperText={errors.end_hour?.message}
              />
            )}
          />
        </Grid>

        {/* Days of Week */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Calling Days
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {daysOfWeek.map((day) => (
              <FormControlLabel
                key={day.value}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedDays.includes(day.value)}
                    onChange={() => handleDayToggle(day.value)}
                  />
                }
                label={day.label}
                sx={{ margin: 0 }}
              />
            ))}
          </Box>
          {errors.days_of_week && (
            <Alert severity="error" sx={{ mt: 0.5 }}>
              {errors.days_of_week.message}
            </Alert>
          )}
        </Grid>


        {/* Submit Button */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            {onCancel && (
              <Button
                variant="outlined"
                onClick={onCancel}
                disabled={creating}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={!isFormReady()}
              startIcon={creating ? <CircularProgress size={20} /> : null}
            >
              {creating ? 'Loading...' : 'Preview Contacts'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NewCampaignForm;
